/**
 * The four phases a person reads a run in (Q26): every internal stage belongs to one, so the
 * run page and the experiment page tell the same story instead of eleven equal dots.
 */
export const RUN_PHASES = ['PREPARE', 'CHECK_QUALITY', 'GENERATE', 'SHIP'] as const;
export type RunPhase = (typeof RUN_PHASES)[number];

const STAGE_PHASE: Record<string, RunPhase> = {
  IMPORT: 'PREPARE',
  FETCH: 'PREPARE',
  IMAGE_GATE: 'PREPARE',
  ARM_ASSIGNMENT: 'PREPARE',
  SPLIT: 'PREPARE',
  CURATION: 'PREPARE',
  PRE_ENRICH_SCORE: 'CHECK_QUALITY',
  SAMPLE_SELECT: 'CHECK_QUALITY',
  SAMPLE: 'CHECK_QUALITY',
  ENRICHMENT: 'GENERATE',
  POST_ENRICH_SCORE: 'GENERATE',
  ROLLUPS: 'GENERATE',
  INSIGHTS: 'GENERATE',
  APPROVAL: 'SHIP',
  DELIVERY: 'SHIP',
  EXPORT: 'SHIP',
  TERMINAL: 'SHIP',
};

/** The phase a stage belongs to; unknown future stages fall into the phase of their position. */
export function phaseOf(stage: string, fallback: RunPhase = 'GENERATE'): RunPhase {
  return STAGE_PHASE[stage] ?? fallback;
}

/** Groups stages (in flow order) into the four phases, keeping each phase's stages in order. */
export function groupStagesByPhase<T extends { stage: string }>(
  stages: T[],
): { phase: RunPhase; stages: T[] }[] {
  let last: RunPhase = 'PREPARE';
  const groups = new Map<RunPhase, T[]>(RUN_PHASES.map((p) => [p, []]));
  for (const s of stages) {
    last = phaseOf(s.stage, last);
    groups.get(last)?.push(s);
  }
  return RUN_PHASES.map((phase) => ({ phase, stages: groups.get(phase) ?? [] })).filter(
    (g) => g.stages.length > 0,
  );
}
